import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { photoUrl } from "../lib/api";
import { X, Search, ChevronRight, MessageSquare, ImageOff } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending Review" },
  { id: "under_review", label: "Under Review" },
  { id: "info_requested", label: "Awaiting Response" },
  { id: "resubmitted", label: "Resubmitted" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];
const CATEGORIES = [
  ["missing_information", "Missing information"],
  ["unclear_answer", "Unclear application answer"],
  ["photo_issue", "Profile photo issue"],
  ["identity_verification", "Identity verification"],
  ["eligibility", "Eligibility question"],
  ["other", "Other"],
];
const STATUS_COLOR = {
  pending: "text-[#C9A96E] border-[#C9A96E]/40", under_review: "text-[#8EC9E6] border-[#8EC9E6]/40",
  info_requested: "text-[#E0B84A] border-[#E0B84A]/40", resubmitted: "text-[#C78AE0] border-[#C78AE0]/40",
  approved: "text-[#7FD6A3] border-[#7FD6A3]/40", rejected: "text-[#E08A8A] border-[#E08A8A]/40",
};
const ACTIONABLE = ["pending", "under_review", "info_requested", "resubmitted"];
// Required-field source of truth mirrors backend _completeness(). Keys == completeness.missing keys.
const REQUIRED_CHECKLIST = [
  ["display_name", "Display name"],
  ["approved_photo", "Profile photo"],
  ["gender", "Gender"],
  ["about_me", "About Me"],
  ["location", "Location"],
  ["date_of_birth", "Date of birth"],
];
// Message templates (manually selected, fully editable, never auto-sent).
const TEMPLATES = [
  ["Missing profile picture", "Hello, we are reviewing your DateBid profile, but we could not locate a usable profile picture. Please upload a suitable profile photo so we can continue the review."],
  ["Incomplete About Me", "Hello, we are reviewing your DateBid profile and need a little more information in your About Me section. Please update your introduction so we can continue reviewing your profile."],
  ["Additional clarification", "Hello, we are reviewing your DateBid profile and need some additional information before completing the review. Please check your messages and reply with the requested clarification."],
  ["Ready for review", "Thank you for completing your DateBid profile. Your profile is ready for our review."],
];

const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a >= 0 && a < 130 ? a : null;
};

const PhotoImg = ({ photoId, className, testId }) => {
  const [err, setErr] = useState(false);
  if (!photoId || err) {
    return (
      <div data-testid={testId ? `${testId}-fallback` : undefined}
        className={`flex flex-col items-center justify-center bg-[#2a2024] text-[#9E978E] ${className}`}>
        <ImageOff size={18} />
        <span className="text-[9px] mt-1 px-1 text-center">Image unavailable</span>
      </div>
    );
  }
  return <img data-testid={testId} src={photoUrl(photoId)} alt="" onError={() => setErr(true)} className={className} />;
};

export const AdminApplications = () => {
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [reqForm, setReqForm] = useState(null);
  const [preview, setPreview] = useState(false);
  const [msgForm, setMsgForm] = useState(null); // { message }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/review/admin/applications", { params: { filter } }); setRows(r.data.applications); }
    catch (e) { toast.error("Failed to load applications"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((a) => (a.display_name || "").toLowerCase().includes(q) || (a.email || "").toLowerCase().includes(q));
  }, [rows, query]);

  const open = async (uid) => {
    try { const r = await api.get(`/review/admin/applications/${uid}`); setDetail(r.data); setReqForm(null); setMsgForm(null); setPreview(false); }
    catch (e) { toast.error("Failed to load applicant"); }
  };
  const refresh = async () => { if (detail) await open(detail.user.user_id); await load(); };

  const nextPending = async () => {
    const pool = filtered.filter((a) => ACTIONABLE.includes(a.status));
    if (pool.length === 0) { toast.info("No pending applications in this view"); return; }
    const curIdx = detail ? pool.findIndex((a) => a.user_id === detail.user.user_id) : -1;
    const next = pool[(curIdx + 1) % pool.length];
    if (next) await open(next.user_id);
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await refresh(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.blockers) toast.error(`${d.message}: ${d.blockers.join("; ")}`);
      else toast.error(typeof d === "string" ? d : "Action failed");
    } finally { setBusy(false); }
  };

  const sendRequest = async () => {
    await act(() => api.post(`/review/admin/applications/${detail.user.user_id}/request-info`, reqForm),
      "Clarification request sent");
    setReqForm(null); setPreview(false);
  };

  const sendMessage = async () => {
    const body = (msgForm?.message || "").trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await api.post(`/review/admin/applications/${detail.user.user_id}/message`, { message: body });
      toast.success("In-app message sent to applicant");
      setMsgForm(null);
      await refresh();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send message");
    } finally { setBusy(false); }
  };

  const pendingCount = rows.filter((a) => ACTIONABLE.includes(a.status)).length;

  return (
    <div data-testid="admin-applications" className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`app-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs border ${filter === f.id ? "bg-[#541224]/50 text-[#D4AF37] border-[#D4AF37]/40" : "text-[#9E978E] border-white/10 hover:text-[#FBF8F3]"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E978E]" />
          <input data-testid="app-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full rounded-full bg-[#0C0A0A]/60 border border-white/10 pl-9 pr-3 py-2 text-sm text-[#FBF8F3]" />
        </div>
        <span data-testid="app-count" className="text-xs text-[#9E978E]">
          Showing {filtered.length} · <span className="text-[#C9A96E]">{pendingCount} needing review</span>
        </span>
        <button data-testid="app-next-pending" onClick={nextPending}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#541224]/40">
          Next Pending <ChevronRight size={14} />
        </button>
      </div>

      {/* List */}
      <div className="grid gap-3">
        {loading && <div data-testid="app-loading" className="text-sm text-[#9E978E] py-8 text-center">Loading applications…</div>}
        {!loading && filtered.length === 0 && <div data-testid="app-empty" className="text-sm text-[#9E978E] py-8 text-center">No applications match this view.</div>}
        {!loading && filtered.map((a) => (
          <button key={a.user_id} data-testid={`app-row-${a.user_id}`} onClick={() => open(a.user_id)}
            className="flex items-center gap-4 text-left rounded-xl border border-white/8 bg-[#141113]/60 p-3 hover:border-[#D4AF37]/30 transition-colors">
            <div className="h-12 w-12 rounded-full overflow-hidden shrink-0">
              {a.main_photo_id
                ? <PhotoImg photoId={a.main_photo_id} className="h-full w-full object-cover" />
                : <div className="h-full w-full bg-[#2a2024] flex items-center justify-center text-[#9E978E]">{(a.display_name || "?")[0]}</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[#FBF8F3] text-sm font-medium truncate">{a.display_name || "Unnamed"}</span>
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_COLOR[a.status] || "text-[#9E978E] border-white/10"}`}>{a.status.replace("_", " ")}</span>
                {a.new_response && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C78AE0]/15 text-[#C78AE0]">New response</span>}
                {a.owes_response && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E0B84A]/15 text-[#E0B84A]">Awaiting applicant</span>}
              </div>
              <div className="text-xs text-[#9E978E] mt-0.5 truncate">
                {a.missing.length ? `Missing: ${a.missing.join(", ")}` : "All required fields present"} · {a.email || "—"}
              </div>
            </div>
          </button>
        ))}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" onClick={() => setDetail(null)}>
          <div data-testid="app-detail" className="relative w-full max-w-4xl my-6 rounded-2xl border border-[#D4AF37]/20 bg-[#141113]" onClick={(e) => e.stopPropagation()}>
            <button data-testid="app-detail-close" onClick={() => setDetail(null)} className="absolute top-3 right-3 text-[#9E978E] hover:text-[#FBF8F3] z-10"><X size={18} /></button>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <h2 className="font-serif text-2xl text-[#FBF8F3]">{detail.profile.display_name || "Applicant"}</h2>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${STATUS_COLOR[detail.status]}`}>{detail.status.replace("_", " ")}</span>
              </div>

              <div className="grid md:grid-cols-5 gap-6">
                {/* LEFT: photo + identity */}
                <div className="md:col-span-2 space-y-4">
                  <div className="rounded-xl overflow-hidden border border-white/10 bg-[#0C0A0A]/50 aspect-[4/5] cursor-pointer"
                    onClick={() => detail.profile.main_photo_id && setLightbox(detail.profile.main_photo_id)}>
                    {detail.profile.main_photo_id
                      ? <PhotoImg testId="app-main-photo" photoId={detail.profile.main_photo_id} className="h-full w-full object-cover" />
                      : <div data-testid="app-no-photo" className="h-full w-full flex flex-col items-center justify-center text-[#9E978E] gap-2"><ImageOff size={28} /><span className="text-xs">No profile picture uploaded</span></div>}
                  </div>
                  {detail.photos.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] text-[#C9A96E] mb-2">All photos ({detail.photos.length})</div>
                      <div className="flex flex-wrap gap-2">
                        {detail.photos.map((p) => (
                          <button key={p.photo_id} data-testid={`app-photo-${p.photo_id}`} onClick={() => setLightbox(p.photo_id)}
                            className="relative h-20 w-16 rounded-lg overflow-hidden border border-white/10">
                            <PhotoImg photoId={p.photo_id} className="h-full w-full object-cover" />
                            {p.is_private && <span className="absolute bottom-0 inset-x-0 text-[9px] bg-black/70 text-[#E0B84A] text-center">private</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                    {[
                      ["Age", ageFromDob(detail.user.date_of_birth) ?? "Not provided"],
                      ["Gender", detail.profile.gender],
                      ["Location", detail.profile.city || detail.profile.location || detail.profile.market_id],
                      ["Created", (detail.submitted_at || "").slice(0, 10)],
                      ["Status", detail.status.replace("_", " ")],
                    ].map(([k, v]) => (
                      <React.Fragment key={k}><dt className="text-[#9E978E]">{k}</dt><dd className="text-[#FBF8F3] truncate">{v == null || v === "" ? "Not provided" : String(v)}</dd></React.Fragment>
                    ))}
                  </dl>
                </div>

                {/* RIGHT: details + completeness */}
                <div className="md:col-span-3 space-y-5">
                  <Section title="Required fields">
                    <ul className="grid sm:grid-cols-2 gap-1.5 text-sm">
                      {REQUIRED_CHECKLIST.map(([key, label]) => {
                        const ok = !detail.completeness.missing.includes(key);
                        return (
                          <li key={key} data-testid={`req-${key}`} className="flex items-center gap-2">
                            <span className={ok ? "text-[#7FD6A3]" : "text-[#E08A8A]"}>{ok ? "✓" : "✗"}</span>
                            <span className="text-[#C9C2BA]">{label}</span>
                            <span className={`ml-auto text-[10px] uppercase ${ok ? "text-[#7FD6A3]" : "text-[#E08A8A]"}`}>{ok ? "Complete" : "Missing"}</span>
                          </li>
                        );
                      })}
                    </ul>
                    <div data-testid="app-readiness" className={`mt-2 text-sm ${detail.completeness.complete ? "text-[#7FD6A3]" : "text-[#E0B84A]"}`}>
                      {detail.completeness.complete ? "All required fields present — ready for an approval decision." : `Not ready: missing ${detail.completeness.missing.join(", ")}.`}
                    </div>
                    <div className="text-xs text-[#9E978E] mt-1">Verification — selfie: {detail.verification.selfie_verified ? "verified" : (detail.verification.has_selfie ? "submitted" : "none")}, ID: {detail.verification.id_verified ? "verified" : (detail.verification.has_id ? "submitted" : "none")}</div>
                  </Section>

                  <Section title="About Me">
                    <div className="text-sm text-[#FBF8F3] whitespace-pre-wrap">{detail.profile.about_me || "Not provided"}</div>
                  </Section>

                  <Section title="Optional details">
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                      {[["Occupation", detail.profile.occupation], ["Height (cm)", detail.profile.height_cm],
                        ["Primary intention", detail.profile.primary_intention], ["Preferred offer", detail.profile.preferred_offer],
                        ["Email", detail.user.email], ["DOB", detail.user.date_of_birth]].map(([k, v]) => (
                        <React.Fragment key={k}><dt className="text-[#9E978E]">{k}</dt><dd className="text-[#FBF8F3] truncate">{v == null || v === "" ? "Not provided" : String(v)}</dd></React.Fragment>
                      ))}
                    </dl>
                    <Field label="Interests" value={(detail.profile.interests || []).join(", ")} />
                    <Field label="Dating intentions" value={(detail.profile.dating_intentions || []).join(", ")} />
                    <Field label="Date preferences" value={(detail.profile.date_preferences || []).join(", ")} />
                  </Section>

                  <Section title={`Clarification Requests (${detail.clarification_requests.length})`}>
                    {detail.clarification_requests.length === 0 ? <div className="text-sm text-[#9E978E]">None.</div> : detail.clarification_requests.map((r) => (
                      <div key={r.request_id} className="rounded-lg border border-white/8 p-3 mb-2 text-sm">
                        <div className="flex items-center justify-between"><span className="text-[#D4AF37] text-xs uppercase">{r.category.replace("_", " ")}</span><span className="text-[10px] text-[#9E978E]">{r.status}</span></div>
                        <div className="text-[#FBF8F3] mt-1">{r.message}</div>
                        <div className="text-[#9E978E] text-xs mt-1">Action requested: {r.required_action}</div>
                        {r.response && <div className="mt-2 rounded bg-[#C78AE0]/10 p-2 text-[#E7D5F0]">Applicant: {r.response}</div>}
                      </div>
                    ))}
                  </Section>

                  <Section title="Review History">
                    {detail.history.length === 0 ? <div className="text-sm text-[#9E978E]">No actions yet.</div> : (
                      <ul className="space-y-1 text-xs text-[#9E978E] max-h-40 overflow-y-auto">
                        {detail.history.map((h) => (
                          <li key={h.review_id}>• <span className="text-[#FBF8F3]">{h.action.replace("_", " ")}</span> {h.prev_status}→{h.new_status} · {(h.created_at || "").slice(0, 16).replace("T", " ")}{h.message ? ` — ${h.message}` : ""}</li>
                        ))}
                      </ul>
                    )}
                  </Section>
                </div>
              </div>

              {/* Message composer */}
              {msgForm !== null && (
                <div className="mt-5 pt-4 border-t border-white/8">
                  <Section title="Message Applicant (in-app only — no email is sent)">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {TEMPLATES.map(([label, text]) => (
                        <button key={label} data-testid={`msg-template-${label.replace(/\s+/g, "-").toLowerCase()}`}
                          onClick={() => setMsgForm({ message: text })}
                          className="px-2.5 py-1 rounded-full text-[11px] border border-white/15 text-[#C9A96E] hover:border-[#D4AF37]/40">{label}</button>
                      ))}
                    </div>
                    <textarea data-testid="msg-body" value={msgForm.message} onChange={(e) => setMsgForm({ message: e.target.value })}
                      placeholder="Write a message to the applicant…" rows={4}
                      className="w-full rounded-lg bg-[#0C0A0A]/60 border border-white/10 px-3 py-2 text-sm text-[#FBF8F3]" />
                    <div className="text-[11px] text-[#9E978E] mt-1">Delivered as an in-app notification. The applicant sees it when they sign in. Approval status is not changed.</div>
                    <div className="flex gap-2 mt-2">
                      <button data-testid="msg-send-btn" disabled={busy || !msgForm.message.trim()} onClick={sendMessage}
                        className="px-4 py-2 rounded-full bg-[#D4AF37] text-[#1a1113] text-sm font-semibold disabled:opacity-40">Send in-app message</button>
                      <button data-testid="msg-cancel-btn" onClick={() => setMsgForm(null)} className="px-4 py-2 rounded-full border border-white/15 text-[#9E978E] text-sm">Cancel</button>
                    </div>
                  </Section>
                </div>
              )}

              {/* Request-info form */}
              {reqForm !== null && (
                <div className="mt-5 pt-4 border-t border-white/8">
                  <Section title="Request Additional Information (keeps applicant pending)">
                    {!preview ? (
                      <div className="space-y-2">
                        <select data-testid="req-category" value={reqForm.category} onChange={(e) => setReqForm({ ...reqForm, category: e.target.value })} className="w-full rounded-lg bg-[#0C0A0A]/60 border border-white/10 px-3 py-2 text-sm text-[#FBF8F3]">
                          {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <textarea data-testid="req-message" value={reqForm.message} onChange={(e) => setReqForm({ ...reqForm, message: e.target.value })} placeholder="Explain the specific issue…" rows={3} className="w-full rounded-lg bg-[#0C0A0A]/60 border border-white/10 px-3 py-2 text-sm text-[#FBF8F3]" />
                        <textarea data-testid="req-action" value={reqForm.required_action} onChange={(e) => setReqForm({ ...reqForm, required_action: e.target.value })} placeholder="What must the applicant provide or correct?" rows={2} className="w-full rounded-lg bg-[#0C0A0A]/60 border border-white/10 px-3 py-2 text-sm text-[#FBF8F3]" />
                        <input data-testid="req-deadline" type="date" value={reqForm.deadline || ""} onChange={(e) => setReqForm({ ...reqForm, deadline: e.target.value })} className="rounded-lg bg-[#0C0A0A]/60 border border-white/10 px-3 py-2 text-sm text-[#FBF8F3]" />
                        <div className="flex gap-2">
                          <button data-testid="req-preview-btn" disabled={!reqForm.message.trim() || !reqForm.required_action.trim()} onClick={() => setPreview(true)} className="px-4 py-2 rounded-full bg-[#541224] text-[#FBF8F3] text-sm disabled:opacity-40">Preview</button>
                          <button data-testid="req-cancel-btn" onClick={() => setReqForm(null)} className="px-4 py-2 rounded-full border border-white/15 text-[#9E978E] text-sm">Cancel / discard draft</button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="rounded-lg border border-[#D4AF37]/30 bg-[#0C0A0A]/60 p-3 text-sm">
                          <div className="text-xs text-[#9E978E] mb-1">This will be delivered to <span className="text-[#FBF8F3]">{detail.profile.display_name}</span>:</div>
                          <div className="text-[#FBF8F3]">{reqForm.message}</div>
                          <div className="text-[#C9A96E] mt-1">What to do: {reqForm.required_action}</div>
                          {reqForm.deadline && <div className="text-xs text-[#9E978E] mt-1">Respond by {reqForm.deadline}</div>}
                        </div>
                        <div className="flex gap-2">
                          <button data-testid="req-send-btn" disabled={busy} onClick={sendRequest} className="px-4 py-2 rounded-full bg-[#D4AF37] text-[#1a1113] text-sm font-semibold disabled:opacity-40">Send request</button>
                          <button data-testid="req-back-btn" onClick={() => setPreview(false)} className="px-4 py-2 rounded-full border border-white/15 text-[#9E978E] text-sm">Back to edit</button>
                        </div>
                      </div>
                    )}
                  </Section>
                </div>
              )}

              {/* Actions */}
              {reqForm === null && msgForm === null && (
                <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-white/8">
                  <button data-testid="app-approve-btn" disabled={busy} onClick={() => act(() => api.post(`/review/admin/applications/${detail.user.user_id}/approve`, { confirm: true }), "Application approved")} className="px-4 py-2 rounded-full bg-[#1f6b43] text-[#daffe8] text-sm font-semibold disabled:opacity-40">Approve Profile</button>
                  <button data-testid="app-requestinfo-btn" onClick={() => { setReqForm({ category: "missing_information", message: "", required_action: "", deadline: "" }); setPreview(false); }} className="px-4 py-2 rounded-full bg-[#5a4a16] text-[#E0B84A] text-sm font-semibold">Request More Information</button>
                  <button data-testid="app-message-btn" onClick={() => setMsgForm({ message: "" })} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2d2140] text-[#C4A0F0] text-sm font-semibold"><MessageSquare size={14} />Message User</button>
                  <button data-testid="app-reject-btn" disabled={busy} onClick={() => { const reason = window.prompt("Reject this profile? Enter a rejection reason (required):"); if (reason && reason.trim()) act(() => api.post(`/review/admin/applications/${detail.user.user_id}/reject`, { reason: reason.trim() }), "Application rejected"); }} className="px-4 py-2 rounded-full bg-[#6b1f28] text-[#ffd9dd] text-sm font-semibold disabled:opacity-40">Reject Profile</button>
                  <button data-testid="app-next-btn" onClick={nextPending} className="ml-auto flex items-center gap-1 px-4 py-2 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-sm">Next Pending <ChevronRight size={14} /></button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div data-testid="app-lightbox" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-6" onClick={() => setLightbox(null)}>
          <img src={photoUrl(lightbox)} alt="" className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div><h3 className="text-xs uppercase tracking-[0.15em] text-[#C9A96E] mb-2">{title}</h3>{children}</div>
);
const Field = ({ label, value }) => (
  <div className="mt-2"><div className="text-[#9E978E] text-xs">{label}</div><div className="text-[#FBF8F3] text-sm whitespace-pre-wrap">{value || "Not provided"}</div></div>
);

export default AdminApplications;
